/*
 * Copyright (c) 2000-2022 TeamDev. All rights reserved.
 * TeamDev PROPRIETARY and CONFIDENTIAL.
 * Use is subject to license terms.
 */

import {defineStore} from 'pinia';
import {User} from '@/api/auth';

/**
 * The structure of the Firebase auth store that is used outside the store.
 *
 * <p>Declares the ID and action names which are being used in `$onAction()` subscribers.
 */
export const FIREBASE_AUTH = {
  ID: 'firebaseAuth',
  ACTION: {
    SET_USER: 'setUser',
  },
};

/**
 * Stores current authentication state of the app.
 */
const useFirebaseAuthStore = defineStore(FIREBASE_AUTH.ID, {
  state: () => ({
    /**
     * The profile of the currently authenticated user.
     *
     * @type {?User}
     */
    user: null,
    /**
     * Indicates that a user has attempted to sign-in, but failed.
     *
     * @type {boolean}
     */
    signInFailed: false,
  }),
  getters: {
    /**
     * Indicates that a user is signed-in.
     *
     * @return {boolean}
     */
    signedIn(state) {
      return !!state.user;
    },

    /**
     * Indicates that a user is not signed-in.
     *
     * @return {boolean}
     */
    notSignedIn(state) {
      return !state.user;
    }
  },
  actions: {
    setUser(value: User|null) {
      this.user = value;
    },
    setSignInFailed() {
      this.user = null;
      this.signInFailed = true;
    }
  },
});

export default useFirebaseAuthStore;
